// Permission action types
export const PERMISSION_ACTIONS = {
  VIEW: 'view',
  ADD: 'add',
  CHANGE: 'change',
  DELETE: 'delete',
} as const

type PermissionAction = (typeof PERMISSION_ACTIONS)[keyof typeof PERMISSION_ACTIONS]
type CustomPermission = (typeof CUSTOM_PERMISSIONS)[keyof typeof CUSTOM_PERMISSIONS]

// Model Definitions
export const ENGAGEMENT_MODELS = {
  ORDER: 'order',
  CASE: 'case',
  SERVICE_POINT: 'servicepoint',
  FINANCIAL_DETAIL: 'financialdetail',
  ENTITY: 'entity',
  CONTACT: 'contact',
  PASS: 'passrecord',
} as const

type EngagementModel = (typeof ENGAGEMENT_MODELS)[keyof typeof ENGAGEMENT_MODELS]

export const MRF_MODELS = {
  ACTIVITY: 'activity',
  CONSIGNMENT: 'consignment',
  INBOUND: 'inbound',
  INVENTORY_ITEM: 'inventoryitem',
  MATERIAL: 'material',
  PARCEL: 'parcel',
  PROCESS: 'process',
  NORMALIZATION_FACTOR: 'normalizationfactor',
} as const

type MRFModel = (typeof MRF_MODELS)[keyof typeof MRF_MODELS]

export const CUSTOM_PERMISSIONS = {
  ADMIN: 'admin',
} as const

// Type that represents all possible permission strings
type PermissionString =
  | `engagement.${PermissionAction}_${EngagementModel}`
  | `MRF.${PermissionAction}_${MRFModel}`
  | CustomPermission

export type Permission = PermissionString

// Create permissions for each model with proper typing
type ModelPermissions<T extends string> = {
  [K in T]: {
    [A in Uppercase<keyof typeof PERMISSION_ACTIONS>]: Permission
  }
}

const createModelPermissions = <M extends 'engagement' | 'MRF', T extends Record<string, string>>(
  module: M,
  models: T,
): ModelPermissions<keyof T & string> => {
  return Object.entries(models).reduce(
    (acc, [modelKey, modelName]) => {
      type PermissionKey = Uppercase<keyof typeof PERMISSION_ACTIONS & string>

      const permissions = Object.entries(PERMISSION_ACTIONS).reduce(
        (permAcc, [actionKey, actionValue]) => {
          const permissionKey = actionKey as PermissionKey
          const permissionValue = `${module}.${actionValue}_${modelName}` as const
          return {
            ...permAcc,
            [permissionKey]: permissionValue as Permission,
          }
        },
        {} as Record<PermissionKey, Permission>,
      )

      return {
        ...acc,
        [modelKey]: permissions,
      }
    },
    {} as ModelPermissions<keyof T & string>,
  )
}

// Create a type-safe object for custom permissions
const createCustomPermissions = <T extends Record<string, string>>(permissions: T) => {
  return Object.fromEntries(
    Object.entries(permissions).map(([key, value]) => [key, value as CustomPermission]),
  ) as { [K in keyof T]: CustomPermission }
}

// Export Permissions
export const PERMISSIONS = {
  ENGAGEMENT: createModelPermissions('engagement', ENGAGEMENT_MODELS),
  MRF: createModelPermissions('MRF', MRF_MODELS),
  CUSTOM: createCustomPermissions(CUSTOM_PERMISSIONS),
} as const
